<template>
  <div
    v-if="isActive && shouldBeVisibleComputed"
    id="banner"
    ref="bannerRef"
    v-editable="blok"
  >
    <transition name="SlideUpFade">
      <div
        v-if="isOpen"
        v-observe-visibility="visibilityObserver"
        class="relative z-50 flex w-full items-center text-base leading-5 text-white"
        :style="{
          'background-color': `${backgroundColor}`,
        }"
      >
        <section
          class="ml-5 mr-12 flex w-full flex-col items-center justify-center text-center sm:relative sm:flex-row"
          :class="{ 'sm:flex-col': hasScrollableLinks }"
        >
          <slot name="body">
            <div
              class="flex w-full flex-col items-center justify-center gap-1 py-2 md:flex-row md:gap-5"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="content" />
              <Countdown
                v-if="
                  hasCountdown && blok.counter_is_active && blok.countdown_until
                "
                :until="blok.countdown_until"
              />
              <ScrollableLinkList
                v-if="hasScrollableLinks"
                :links="blok.links"
                @click:promotion-click="onPromotionClick"
              />
            </div>
          </slot>
        </section>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { isEmpty } from 'es-toolkit/compat'
import type { SbBanner } from '~/cms/storyblok/types/storyblok.d.ts'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

type BannerProps = {
  background?: string
  blok?: SbBanner & {
    published_at: string
    background_color?: { color: string }
  }
}

const props = withDefaults(defineProps<BannerProps>(), {
  background: 'black',
  blok: () =>
    ({}) as SbBanner & {
      published_at: string
      background_color?: { color: string }
    },
})

const { isOpen, shouldBeVisible } = useBanner()
const { content } = useRichtext(props.blok.body)
const bannerRef = ref<HTMLDivElement>()
const { bannerHeight } = useUiState()

const { trackPromotion, trackPromotionSelect } = useTrackingEvents()
const observerOptions = {
  intersection: { threshold: 0.5 },
  callback: () => {},
}

const debouncedHandleResize = useDebounceFn(() => {
  bannerHeight.value = bannerRef.value?.offsetHeight || 0
}, 100)


watch(bannerRef, debouncedHandleResize)

useEventListener('resize', debouncedHandleResize)

const isActive = computed(() =>
  isEmpty(props.blok) ? true : props.blok.is_active,
)
const shouldBeVisibleComputed = computed(() =>
  shouldBeVisible(props.blok.published_at),
)
const hasCountdown = computed(
  () => !isEmpty(props.blok) && props.blok.countdown_until !== '',
)
const hasScrollableLinks = computed(
  () => !isEmpty(props.blok) && !isEmpty(props.blok.links),
)

const visibilityObserver = props.blok.promotion_id ? observerOptions : null

const onPromotionClick = ({
  clickPathCategory,
  url,
}: {
  clickPathCategory: string
  url: string
}) => {
  trackPromotionSelect({
    id: props.blok.promotion_id || '',
    name: props.blok.promotion_name || '',
    pageType: 'all',
    promoType: 'headerbanner',
    position: 1,
    creativeNameSb: props.blok.creative_name || '',
  })

  trackPromotion(
    'select_promotion',
    {
      promotion_name: props.blok.promotion_name || 'black_banner',
      location_id: props.blok.location_id || clickPathCategory,
      item_id: props.blok.item_id || '',
      item_name: props.blok.item_name || '',
      promotion_id: props.blok.promotion_id || '1',
      creative_name: props.blok.creative_name || '',
      creative_slot: props.blok.creative_slot || '',
    },
    { url },
  )
}

const backgroundColor = computed(
  () => props.blok.background_color?.color || '#000000',
)
</script>
